<script setup lang="ts">
    import type { Timeslot } from "common";
    import { ignorableWatch } from "@vueuse/core";
    import { useOrderStore } from "~/composables/store/order";
    const orderStore = useOrderStore();

    const { d } = useI18n();

    const r$ = orderStore.validation.r$;

    const selectedDate = ref();

    onMounted(() => {
        ignoreSelectedDateUpdates(() => {
            if (orderStore.currentOrder.fulfillment.timeslot) {
                selectedDate.value = orderStore.options.timeslots.find((day) =>
                    day.slots.some(
                        (slot) =>
                            slot.id ===
                            orderStore.currentOrder.fulfillment.timeslot,
                    ),
                )?.date;
            }
        });
    });

    const availableDays = computed(() =>
        orderStore.meta.availableTimeslots.map((day) => {
            return {
                value: day.date,
                title: capitalize(d(day.date, "long")),
            };
        }),
    );

    const availableTimeslots = computed(() => {
        const selectedDay = orderStore.meta.availableTimeslots.find(
            (day) => selectedDate.value === day.date,
        );
        if (!selectedDay) {
            return [];
        }

        const formatTime = (item: Timeslot): string => {
            const startTime = item.start.slice(0, -3);
            const endTime = item.end.slice(0, -3);
            return `${startTime} - ${endTime}`;
        };

        return selectedDay.slots.map((timeslot) => {
            return {
                value: timeslot.id,
                title: formatTime(timeslot),
            };
        });
    });

    const hasProductsSelected = computed(() => {
        return orderStore.currentOrder.products.some(
            (product) => product.amount > 0,
        );
    });

    // If the date is changed, reset the timeslot
    const { ignoreUpdates: ignoreSelectedDateUpdates } = ignorableWatch(
        () => selectedDate.value,
        () => {
            orderStore.currentOrder.fulfillment.timeslot = undefined;
        },
    );

    watch(
        () => availableTimeslots,
        (
            newAvailableTimeslots: ComputedRef<
                { value: number; title: string }[]
            >,
        ) => {
            if (!newAvailableTimeslots.value.length) {
                // If there are no available timeslots, reset the selected date
                selectedDate.value = undefined;
            }

            // if (!newAvailableTimeslots.some((day) => day.date === selectedDate.value)) {
            //   // If the selected date is not available, reset it to the first available date
            //   selectedDate.value = undefined;
            // }
        },
        { immediate: true, deep: true },
    );

    // watch(
    //   () => orderStore.currentOrder.fulfillment.timeslot,
    //   (newValue) => {
    //     if (newValue === undefined) {
    //       // Set selected date to undefined if timeslot is cleared and no timeslot with current date is available
    //       const selectedDay = orderStore.meta.availableTimeslots.find(
    //         (day) => selectedDate.value === day.date
    //       );

    //       if (selectedDay === undefined || selectedDay.slots.length === 0) {
    //         selectedDate.value = undefined;
    //       }
    //     }
    //   }
    // );
</script>

<template>
    <shared-labeled-field
        v-slot="{ ariaLabelledby }"
        :label="$t('checkout.timeslot.dayLabel')"
    >
        <VSelect
            v-model="selectedDate"
            :aria-labelledby="ariaLabelledby"
            :item-props="true"
            :disabled="!hasProductsSelected"
            :items="availableDays"
        />
    </shared-labeled-field>
    <shared-labeled-field
        v-slot="{ ariaLabelledby }"
        :label="$t('checkout.timeslot.timeLabel')"
    >
        <VSelect
            v-model="orderStore.currentOrder.fulfillment.timeslot"
            :aria-labelledby="ariaLabelledby"
            :item-props="true"
            :disabled="!(hasProductsSelected && selectedDate)"
            :items="availableTimeslots"
            :error-messages="r$.$errors.fulfillment.timeslot"
            @focusout="r$.$fields.fulfillment.timeslot.$touch"
        />
    </shared-labeled-field>
    <shared-alert
        v-if="orderStore.meta.selectedTimeslot?.alert"
        :alert="orderStore.meta.selectedTimeslot.alert"
    />
</template>
